<app-protected-content [requiredRoles]="requiredRoles">
  @let uc = useCase();

  @if (uc) {
    <!-- HERO -->
    <htx-hero-section
      [title]="uc.titleKey | transloco"
      [subtitle]="uc.subtitleKey | transloco"
      [metaItems]="heroMetaItems()"
      [flatBottom]="true"
    >
    </htx-hero-section>

    <!-- PAGE NAV (sticky) -->
    <nav class="hero-nav">
      @for (item of pageNavItems(); track item.targetId) {
        <a href="javascript:void(0)" (click)="scrollTo(item.targetId)">
          <htx-icon [icon]="item.icon"></htx-icon>
          {{ item.label }}
        </a>
      }
    </nav>

    <!-- CONTENT -->
    <div class="detail-content">
      <!-- BESCHREIBUNG -->
      <div class="section" id="beschreibung">
        <h3 class="section-title">
          <htx-icon icon="description"></htx-icon>
          {{ 'usecases.sections.descriptionTitle' | transloco }}
        </h3>
        <div class="description-text" [innerHTML]="uc.descriptionKey | transloco"></div>
      </div>

      <!-- ROLLEN -->
      <div class="section" id="rollen">
        <h3 class="section-title">
          <htx-icon icon="people"></htx-icon>
          {{ 'usecases.sections.rolesTitle' | transloco }}
        </h3>
        <htx-info-card-grid [cards]="roleCards()" variant="card"></htx-info-card-grid>
      </div>

      <!-- SOFTWARE & SCREENSHOTS -->
      <div class="section" id="software">
        <h3 class="section-title">
          <htx-icon icon="integration_instructions"></htx-icon>
          {{ 'usecases.sections.softwareTitle' | transloco }}
        </h3>
        @for (sw of uc.software; track sw.title; let idx = $index) {
          @if (sw.groupKey && sw.groupKey !== uc.software[idx - 1]?.groupKey) {
            <h4 class="software-group-title">{{ sw.groupKey | transloco }}</h4>
          }
          <div class="software-card">
            <div class="software-card-header">
              <div class="software-info">
                <h4>{{ sw.title | transloco }}</h4>
                <p>{{ sw.description | transloco }}</p>
                <div class="software-status">
                  <htx-icon icon="schedule"></htx-icon>
                  {{ sw.status | transloco }}
                </div>
              </div>
            </div>
            @if (sw.screenshots.length > 0) {
              <div class="software-screenshots">
                @for (screenshot of sw.screenshots; track screenshot.src) {
                  <div
                    class="software-screenshot-card"
                    role="button"
                    tabindex="0"
                    (click)="openLightbox(screenshot.src, screenshot.caption)"
                    (keydown.enter)="openLightbox(screenshot.src, screenshot.caption)"
                  >
                    <img
                      [src]="screenshot.src"
                      [alt]="screenshot.caption | transloco"
                      loading="lazy"
                    />
                    <div class="caption">{{ screenshot.caption | transloco }}</div>
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>

      <!-- DIAGRAMM -->
      @if (uc.diagram) {
        <div class="section" id="diagramm">
          <h3 class="section-title">
            <htx-icon icon="schema"></htx-icon>
            {{ 'usecases.sections.diagramTitle' | transloco }}
          </h3>
          <div
            class="diagram-container"
            role="button"
            tabindex="0"
            (click)="openLightbox(uc.diagram.src, uc.diagram.caption)"
            (keydown.enter)="openLightbox(uc.diagram.src, uc.diagram.caption)"
          >
            <img [src]="uc.diagram.src" [alt]="uc.diagram.caption | transloco" loading="lazy" />
            <div class="caption">{{ uc.diagram.caption | transloco }}</div>
          </div>
        </div>
      }

      <!-- DOKUMENTE -->
      @if (uc.documents.length > 0) {
        <div class="section" id="dokumente">
          <h3 class="section-title">
            <htx-icon icon="description"></htx-icon>
            {{ 'usecases.sections.documentsTitle' | transloco }}
          </h3>
          <div class="document-list">
            @for (doc of uc.documents; track doc.url) {
              <a class="document-card" [href]="doc.url" [download]="doc.filename">
                <span class="document-icon">
                  <htx-icon icon="picture_as_pdf"></htx-icon>
                </span>
                <span class="document-title">{{ doc.titleKey | transloco }}</span>
                <htx-icon class="document-download" icon="download"></htx-icon>
              </a>
            }
          </div>
        </div>
      }

      <!-- VORAUSSETZUNGEN -->
      <div class="section" id="voraussetzungen">
        <h3 class="section-title">
          <htx-icon icon="checklist"></htx-icon>
          {{ 'usecases.sections.prerequisitesTitle' | transloco }}
        </h3>
        <htx-checklist [items]="prerequisiteItems()"></htx-checklist>
      </div>

      <!-- FAQ -->
      <div class="section" id="faq">
        <h3 class="section-title">
          <htx-icon icon="help_outline"></htx-icon>
          {{ 'usecases.sections.faqTitle' | transloco }}
        </h3>
        <htx-faq-accordion [items]="faqItems()"></htx-faq-accordion>
      </div>

      <!-- LIGHTBOX -->
      @if (lightboxSrc()) {
        <div
          class="lightbox-overlay"
          role="dialog"
          tabindex="0"
          (click)="closeLightbox()"
          (keydown.escape)="closeLightbox()"
        >
          <div
            class="lightbox-content"
            tabindex="-1"
            (click)="$event.stopPropagation()"
            (keydown.escape)="closeLightbox()"
          >
            <button class="lightbox-close" (click)="closeLightbox()">
              <htx-icon icon="close"></htx-icon>
            </button>
            <img [src]="lightboxSrc()" [alt]="lightboxAlt()" />
            <div class="lightbox-caption">{{ lightboxAlt() }}</div>
          </div>
        </div>
      }

      <!-- CTA -->
      <htx-cta-section
        [title]="'usecases.cta.title' | transloco"
        [description]="'usecases.cta.description' | transloco"
      >
        @if (!canParticipate()) {
          <htx-button variant="secondary" icon="lock" [disabled]="true">
            {{ 'usecases.cta.insufficientRole' | transloco }}
          </htx-button>
        } @else if (orgNotApproved()) {
          <htx-button variant="secondary" icon="block" [disabled]="true">
            {{ 'usecases.cta.orgNotApproved' | transloco }}
          </htx-button>
        } @else if (orgInactive()) {
          <htx-button variant="secondary" icon="block" [disabled]="true">
            {{ 'usecases.cta.orgInactive' | transloco }}
          </htx-button>
        } @else if (participationState() === AdmissionState.APPROVED) {
          <htx-button variant="secondary" icon="check_circle" [disabled]="true">
            {{ 'usecases.cta.statusApproved' | transloco }}
          </htx-button>
        } @else if (participationState() === AdmissionState.REJECTED) {
          <htx-button variant="secondary" icon="cancel" [disabled]="true">
            {{ 'usecases.cta.statusRejected' | transloco }}
          </htx-button>
        } @else if (participationState() === AdmissionState.SUSPENDED) {
          <htx-button variant="secondary" icon="block" [disabled]="true">
            {{ 'usecases.cta.orgInactive' | transloco }}
          </htx-button>
        } @else if (participationState() === AdmissionState.PENDING) {
          <htx-button variant="secondary" icon="hourglass_top" [disabled]="true">
            {{ 'usecases.cta.statusPending' | transloco }}
          </htx-button>
        } @else if (statusLoading()) {
          <htx-button variant="secondary" icon="hourglass_empty" [disabled]="true">
            {{ 'usecases.cta.button' | transloco }}
          </htx-button>
        } @else if (statusError()) {
          <htx-button variant="secondary" icon="error_outline" [disabled]="true">
            {{ 'usecases.cta.button' | transloco }}
          </htx-button>
        } @else {
          <htx-button
            variant="secondary"
            icon="how_to_reg"
            (click)="onRequestParticipation()"
            [disabled]="requestInProgress()"
          >
            {{ 'usecases.cta.button' | transloco }}
          </htx-button>
        }
      </htx-cta-section>
    </div>
  } @else {
    <div class="skeleton-hero">
      <htx-skeleton width="50%" height="2.25rem" borderRadius="6px"></htx-skeleton>
      <htx-skeleton width="70%" height="1.1rem" borderRadius="4px"></htx-skeleton>
    </div>
    <div class="detail-content">
      <htx-skeleton width="30%" height="1.1rem" borderRadius="4px"></htx-skeleton>
      <htx-skeleton
        width="100%"
        height="1rem"
        [lines]="4"
        gap="0.75rem"
        borderRadius="4px"
      ></htx-skeleton>
      <htx-skeleton width="30%" height="1.1rem" borderRadius="4px"></htx-skeleton>
      <htx-skeleton
        width="100%"
        height="7rem"
        [lines]="3"
        gap="1rem"
        borderRadius="8px"
      ></htx-skeleton>
    </div>
  }
</app-protected-content>
